import createActionCreator from "@web-monorepo/infra/createActionCreator";
import createReducer from "@web-monorepo/infra/createReducer";
import { SagaIterator, Task } from "redux-saga";
import { fork, join, race, call, put, delay, takeLatest } from "redux-saga/effects";
import { MicrophoneState } from "src/pods/microphonePermissions";

export const REQUEST_PERMISSION = "microphonePermissions/requestPermission";
export const PERMISSION_ALLOWED = "microphonePermissions/permissionAllowed";
export const PERMISSION_DENIED = "microphonePermissions/permissionDenied";
export const NO_MICROPHONE = "microphonePermissions/noMicrophone";
export const WAITING_ON_USER = "microphonePermissions/waitingOnUser";
export const MICROPHONE_INIT_LAG = 1000;

export const requestPermission = createActionCreator(REQUEST_PERMISSION);

export function* requestPermissionFromBrowserSaga() {
  try {
    // call() with an array param is bound function call
    const mediaStream: MediaStream = yield call([navigator.mediaDevices, navigator.mediaDevices.getUserMedia], {
      audio: true,
    });

    // invokeMap("stop", mediaStream.getAudioTracks());
    Array.from(mediaStream.getAudioTracks()).forEach((track) => track.stop());

    yield put({
      type: PERMISSION_ALLOWED,
    });
    // eslint-disable-next-line no-catch-all/no-catch-all
  } catch (e) {
    // TODO: handle more errors
    if (e.name === "NotAllowedError") {
      yield put({ type: PERMISSION_DENIED });
      return;
    }

    yield put({ type: NO_MICROPHONE });
  }
}

export function* askUserForPermissionsSaga(): SagaIterator {
  // fork requesting permissions because we don't want the race below cancel
  // the permissions saga if the delay wins.
  const requestPermissionsTask: Task = yield fork(requestPermissionFromBrowserSaga);

  // if the user has already given us permissions to their microphone, it still
  // takes some time for the browser to turn on the microphone and tell us it was
  // successful.
  const { awaitMicrophoneLag } = yield race({
    requestPermissions: join(requestPermissionsTask),
    awaitMicrophoneLag: call(delay, MICROPHONE_INIT_LAG),
  });

  if (awaitMicrophoneLag) {
    // delay won the race, meaning we're waiting on user to allow/deny
    yield put({
      type: WAITING_ON_USER,
    });
  }
}

export function* requestPermissionSaga() {
  yield takeLatest(REQUEST_PERMISSION, askUserForPermissionsSaga);
}

export const microphoneRequestPermissionHandler = createReducer(
  REQUEST_PERMISSION,
  (state: MicrophoneState): MicrophoneState => ({
    ...state,
    isRequestingPermission: true,
  }),
);

export const microphonePermissionAllowedHandler = createReducer(
  PERMISSION_ALLOWED,
  () =>
    (state: MicrophoneState): MicrophoneState => ({
      ...state,
      hasMicrophone: true,
      hasPermission: true,
      isRequestingPermission: false,
      isWaitingOnUser: false,
    }),
);

export const microphonePermissionDeniedHandler = createReducer(
  PERMISSION_DENIED,
  () =>
    (state: MicrophoneState): MicrophoneState => ({
      ...state,
      hasMicrophone: true,
      hasPermission: false,
      isRequestingPermission: false,
      isWaitingOnUser: false,
    }),
);

export const microphoneNoMicrophoneHandler = createReducer(
  NO_MICROPHONE,
  () =>
    (state: MicrophoneState): MicrophoneState => ({
      ...state,
      hasMicrophone: false,
      isRequestingPermission: false,
      isWaitingOnUser: false,
    }),
);

export const microphoneWaitingOnUserHandler = createReducer(
  WAITING_ON_USER,
  () =>
    (state: MicrophoneState): MicrophoneState => ({
      ...state,
      isWaitingOnUser: true,
    }),
);
